/**
 * BreakdownList — per-formula-group calculation rows for the hover card.
 *
 * Shows expression + raw/weighted scores only. Full cube stats live in the
 * Performance section above — not repeated here.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';

import { cn } from '@/lib/utils';

import type { PreviewBreakdownEntry } from '../../preview-row.types';
import { fmtScore, rawScoreTone } from '../format';
import { humanizeExpression, type SubformulaInfo } from './calc/score-calculation';

export interface BreakdownListProps {
  entries: PreviewBreakdownEntry[];
  /** Subformula id -> {name, expression}, so `__sub_…` tokens render readably. */
  subFormulas?: SubformulaInfo;
}

const BreakdownList: FC<BreakdownListProps> = ({ entries, subFormulas }) => (
  <ul className="divide-y divide-border/60 rounded border border-border/60">
    {entries.map((entry) => (
      <li key={entry.groupId} className="space-y-1 px-2 py-1.5">
        <BreakdownHeader entry={entry} />
        {entry.expression ? (
          <BreakdownExpression expression={humanizeExpression(entry.expression, subFormulas)} />
        ) : null}
        <BreakdownScores entry={entry} />
      </li>
    ))}
  </ul>
);

const BreakdownHeader: FC<{ entry: PreviewBreakdownEntry }> = ({ entry }) => (
  <div className="flex items-baseline justify-between gap-2">
    <span className="truncate font-medium text-foreground">{entry.groupName}</span>
    <span className="text-[10px] tabular-nums text-muted-foreground">{entry.dateRange}</span>
  </div>
);

const BreakdownExpression: FC<{ expression: string }> = ({ expression }) => (
  <div className="text-[10px] text-muted-foreground">
    <span className="mr-1">expression</span>
    <code className="rounded bg-muted/40 px-1 py-0.5 font-mono text-[10px] text-foreground">
      {expression}
    </code>
  </div>
);

const BreakdownScores: FC<{ entry: PreviewBreakdownEntry }> = ({ entry }) => (
  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-[10px] tabular-nums text-muted-foreground">
    <span>
      raw{' '}
      <span className={cn('font-medium', rawScoreTone(entry.rawScore))}>
        {fmtScore(entry.rawScore)}
      </span>
    </span>
    <span>
      weighted{' '}
      <span className={cn('font-medium', rawScoreTone(entry.weightedScore))}>
        {fmtScore(entry.rawScore)} × {resolveWeight(entry)}% ={' '}
        {fmtScore(entry.weightedScore)}
      </span>
    </span>
  </div>
);

function resolveWeight(entry: PreviewBreakdownEntry): number {
  if (entry.groupWeight != null) return Math.round(entry.groupWeight * 100) / 100;
  if (entry.rawScore === 0) return 0;
  return Math.round((entry.weightedScore / entry.rawScore) * 10000) / 100;
}

export default BreakdownList;
